import { css } from '@emotion/react'
import styled from '@emotion/styled'

import { ArcadeColors, useGetArcadeColor } from '../../theme/arcadeColors'
import { Flex } from '../layout/Flex'
import Text from '../Text/Text'

export interface BadgeProps {
  /**
   * Required: The variant of the badge.
   * asset: used on icons and avatars.
   * inline: used on cells and rows.
   */
  variant: 'asset' | 'inline'
  /**
   * Optional: The number to display in the badge.
   * It can be up to 3 characters long
   * If number is provided, the badge will be displayed as a large badge.
   * Else, the badge will be displayed as a small badge.
   * Edge case: if number is 0, the badge will not be displayed.
   * Ex. 1, 99, 99+, 2+
   */
  number?: string | number
  /**
   * Optional: The stroke to display on the badge.
   * Only applicable to asset variant.
   */
  stroke?: 'default' | 'keypad' | 'bitcoin'
}

const STROKE_MAP: Record<'default' | 'keypad' | 'bitcoin', ArcadeColors> = {
  default: 'borderApp',
  keypad: 'backgroundKeypad',
  bitcoin: 'backgroundBitcoin',
}

/* Size of Asset Badge in pixels */
const ASSET_SIZE_MAP = {
  small: {
    width: 6,
    height: 6,
  },
  large: {
    width: 16,
    height: 16,
  },
}

/* Size of Asset Badge in pixels */
const INLINE_SIZE_MAP = {
  small: {
    width: 8,
    height: 8,
  },
  large: {
    width: 20,
    height: 20,
  },
}

const BadgeWrapper = styled(Flex)<Omit<BadgeProps, 'number'> & { showNumber: boolean; size: 'small' | 'large' }>`
  border-radius: ${({ theme }) => theme.border.radius.pill};
  background-color: ${({ theme }) => theme.arcadeColors.semantic.backgroundDanger};
  ${({ stroke, variant, theme }) =>
    stroke && variant === 'asset' && `outline: ${theme.pxToRem(2)} solid ${useGetArcadeColor(STROKE_MAP[stroke])};;`}
  ${({ showNumber, theme }) => showNumber && `padding: ${theme.spacing.xxs};`}
  ${({ size, variant, showNumber, theme }) => {
    if (variant === 'asset') {
      const assetSize = ASSET_SIZE_MAP[size]
      return css`
        min-width: ${theme.pxToRem(assetSize.width)};
        width: ${showNumber ? 'fit-content' : theme.pxToRem(assetSize.width)};
        height: ${theme.pxToRem(assetSize.height)};
      `
    } else if (variant === 'inline') {
      const inlineSize = INLINE_SIZE_MAP[size]
      return css`
        min-width: ${theme.pxToRem(inlineSize.width)};
        width: ${showNumber ? 'fit-content' : theme.pxToRem(inlineSize.width)};
        height: ${theme.pxToRem(inlineSize.height)};
      `
    }
    return
  }}
`
/**
 * Badge component that is used as a visual indicator within components that is
 * used to convey activity, status, or quantity.
 *
 * Examples of components that Badge component is used within:
 * Cells([Push Cell](?path=/story/arcade-cell-interactivecell--interactive-cell-push-with-small-badge), [Radio Cell](?path=/story/arcade-cell-radiocell--radio-cell-icon-with-small-badge)), Avatar (not implemented yet for web)
 */
export const Badge = ({ variant, number, stroke = 'default' }: BadgeProps) => {
  const showNumber = !!number
  return (
    <BadgeWrapper
      justifyContent="center"
      alignItems="center"
      size={showNumber ? 'large' : 'small'}
      variant={variant}
      stroke={stroke}
      showNumber={showNumber}
    >
      {showNumber && (
        <Text
          typography={variant === 'asset' ? 'badgeLargeAsset' : 'badgeLargeInline'}
          color="textInverse"
          data-testid="badge-text"
        >
          {number}
        </Text>
      )}
    </BadgeWrapper>
  )
}
